import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Mail, Send, Loader2, RotateCw, Ban, CheckCircle2, Clock, AlertTriangle,
  Copy, Link as LinkIcon, Wand2, DollarSign, ExternalLink,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * /admin/users/invite — Send & manage one-link invites.
 *
 * Replaces the seed-a-throwaway-password workflow with a member-of-staff
 * onboarding flow: admin enters email + name + role, system emails a
 * magic link, invitee clicks it and sets their own password. No shared
 * credentials ever transit any channel.
 */
const STATUS_BADGE = {
  pending:  { label: "Pending",  cls: "bg-amber-400/20 text-amber-300 border-amber-400/40" },
  accepted: { label: "Accepted", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  revoked:  { label: "Revoked",  cls: "bg-red-500/20 text-red-300 border-red-500/40" },
};

const AdminUserInvitePage = () => {
  const { user } = useAuth();
  const isSuper = user?.role === "superuser";
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", full_name: "", role: "client", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  // Magic-link tool — admin pastes an existing user's email, gets back a
  // one-time activation URL they can manually email/Slack the user.
  const [magicEmail, setMagicEmail] = useState("");
  const [magicResult, setMagicResult] = useState(null);
  const [magicLoading, setMagicLoading] = useState(false);
  // Pricing sync tool
  const [pricingSyncing, setPricingSyncing] = useState(false);
  const [pricingResult, setPricingResult] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/users/invites");
      setInvites(r.data?.invites || []);
    } catch (err) {
      console.error("AdminUserInvitePage: failed to load invites", err);
      toast.error("Failed to load invites.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendInvite = async () => {
    const email = (form.email || "").trim().toLowerCase();
    const name = (form.full_name || "").trim();
    if (!email || !name) {
      toast.error("Enter both email and full name.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/users/invites", {
        email,
        full_name: name,
        role: form.role,
        notes: form.notes.trim() || undefined,
      });
      if (r.data?.resent) {
        toast.success("A pending invite already existed — re-sent the email.");
      } else if (r.data?.email_sent) {
        toast.success("Invite sent.");
      } else {
        toast.warning("Invite created but the email failed to deliver. Check SMTP settings.");
      }
      setForm({ email: "", full_name: "", role: "client", notes: "" });
      await refresh();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to create invite.";
      toast.error(typeof msg === "string" ? msg : "Failed to create invite.");
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (inviteId) => {
    if (!window.confirm("Revoke this invite? The link will stop working immediately.")) return;
    try {
      await api.post(`/admin/users/invites/${inviteId}/revoke`);
      toast.success("Invite revoked.");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed.");
    }
  };

  const resend = async (inviteId) => {
    try {
      const r = await api.post(`/admin/users/invites/${inviteId}/resend`);
      toast.success(r.data?.email_sent ? "Invite re-sent." : "Resend triggered (email may still be queued).");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed.");
    }
  };

  const copyInvitePreviewLink = async (invite) => {
    // The raw token isn't exposed in the listing response; admins fall back to resending.
    // This button copies the invitee's email instead, so admins can send a manual nudge.
    try {
      await navigator.clipboard.writeText(invite.email);
      toast.success("Invitee email copied — you can now nudge them directly.");
    } catch (err) {
      console.debug("Clipboard copy failed", err);
      toast.error("Copy failed. Email shown in the card.");
    }
  };

  const generateMagicLink = async () => {
    const email = (magicEmail || "").trim().toLowerCase();
    if (!email) { toast.error("Enter the user's email."); return; }
    setMagicLoading(true);
    setMagicResult(null);
    try {
      const r = await api.post("/admin/users/magic-link", { email });
      setMagicResult(r.data);
      toast.success("Magic link generated. Copy it below.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate link.");
    } finally {
      setMagicLoading(false);
    }
  };

  const copyMagicLink = async () => {
    if (!magicResult?.activation_url) return;
    try {
      await navigator.clipboard.writeText(magicResult.activation_url);
      toast.success("Link copied to clipboard.");
    } catch (err) {
      console.debug("Clipboard copy failed", err);
      toast.error("Copy failed. Long-press / right-click to copy manually.");
    }
  };

  const applyCanonicalPricing = async () => {
    if (!window.confirm(
      "Apply canonical pricing ($999 one-time + $99/month) to this environment? " +
      "This is safe to run repeatedly."
    )) return;
    setPricingSyncing(true);
    try {
      const r = await api.post("/admin/settings/sync-pricing");
      setPricingResult(r.data);
      toast.success(`Pricing synced: $${r.data.after.registration_fee} + $${r.data.after.monthly_fee}/mo`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sync pricing.");
    } finally {
      setPricingSyncing(false);
    }
  };

  const roleOptions = isSuper
    ? [
        { v: "client",    l: "Client (member)" },
        { v: "referrer",  l: "Referrer (introducer)" },
        { v: "admin",     l: "Admin" },
        { v: "superuser", l: "Superuser" },
      ]
    : [
        { v: "client",    l: "Client (member)" },
        { v: "referrer",  l: "Referrer (introducer)" },
      ];

  return (
    <DashboardLayout
      title="User Invites"
      subtitle="Send one-click invites — invitee sets their own password. No shared credentials."
    >
      <div className="space-y-6" data-testid="admin-user-invite-page">
        {/* Utility row — Magic Link + Apply Pricing */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Magic activation link for EXISTING users (e.g. Kevin) */}
          <Card className="border-blue-400/30 bg-blue-400/[0.04]" data-testid="magic-link-card">
            <CardHeader>
              <CardTitle className="text-platinum font-serif text-lg flex items-center gap-2">
                <Wand2 className="text-blue-300" size={16} /> Magic Activation Link
              </CardTitle>
              <CardDescription className="text-platinum/60 text-xs leading-relaxed">
                For an <strong>existing</strong> user who can't receive emails. Generates a
                one-time URL — paste it into your own email or Slack to the user. They click,
                set their password, and they're in.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  placeholder="kevin@tryant.ai"
                  className="bg-obsidian border-platinum/15 text-platinum"
                  data-testid="magic-link-email-input"
                />
                <Button
                  onClick={generateMagicLink}
                  disabled={magicLoading}
                  className="bg-blue-400 hover:bg-blue-300 text-slate-900 font-semibold whitespace-nowrap"
                  data-testid="magic-link-generate-btn"
                >
                  {magicLoading
                    ? <Loader2 size={14} className="animate-spin" />
                    : <><Wand2 size={14} className="mr-1.5" /> Generate</>}
                </Button>
              </div>

              {magicResult && (
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] p-3 text-xs space-y-2" data-testid="magic-link-result">
                  <p className="text-emerald-300">
                    Link generated for <span className="font-mono">{magicResult.target_email}</span> ·
                    expires {new Date(magicResult.expires_at).toLocaleDateString()}
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 truncate bg-obsidian/60 rounded px-2 py-1.5 text-slate-300 font-mono text-[11px]">
                      {magicResult.activation_url}
                    </code>
                    <Button
                      size="sm"
                      onClick={copyMagicLink}
                      className="bg-amber-400 hover:bg-amber-300 text-slate-900 shrink-0"
                      data-testid="magic-link-copy-btn"
                    >
                      <Copy size={12} className="mr-1" /> Copy
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-500 leading-relaxed">
                    Single-use. Paste into your own email/Slack/Signal to the user — never share it publicly.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Apply New Pricing — admin button to sync $999 / $99 to system_settings */}
          <Card className="border-amber-400/30 bg-amber-400/[0.04]" data-testid="pricing-sync-card">
            <CardHeader>
              <CardTitle className="text-platinum font-serif text-lg flex items-center gap-2">
                <DollarSign className="text-amber-300" size={16} /> Apply Canonical Pricing
              </CardTitle>
              <CardDescription className="text-platinum/60 text-xs leading-relaxed">
                Push <strong>$999 one-time + $99/month</strong> to this environment's
                system_settings.global. Use this once per environment after deploying a
                pricing change. Idempotent — safe to run repeatedly.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={applyCanonicalPricing}
                disabled={pricingSyncing}
                className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold"
                data-testid="pricing-sync-btn"
              >
                {pricingSyncing
                  ? <><Loader2 size={14} className="mr-2 animate-spin" /> Syncing…</>
                  : <><DollarSign size={14} className="mr-2" /> Apply New Pricing</>}
              </Button>

              {pricingResult && (
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] p-3 text-xs text-emerald-200" data-testid="pricing-sync-result">
                  <p>
                    <span className="text-slate-400">Before:</span>{" "}
                    ${pricingResult.before.registration_fee ?? "—"} + ${pricingResult.before.monthly_fee ?? "—"}/mo
                  </p>
                  <p>
                    <span className="text-slate-400">After:</span>{" "}
                    <strong>${pricingResult.after.registration_fee} + ${pricingResult.after.monthly_fee}/mo</strong>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Compose */}
        <Card className="border-amber-400/20 bg-amber-400/[0.04]">
          <CardHeader>
            <CardTitle className="text-platinum font-serif text-xl flex items-center gap-2">
              <Send className="text-amber-300" size={18} /> Send Invite
            </CardTitle>
            <CardDescription className="text-platinum/60">
              The invitee receives an email with a magic link valid for 7 days. They click,
              set their password, and they're in. Their password never touches your inbox.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-platinum/80 text-sm">Email Address</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="kevin@tryant.ai"
                  className="mt-1.5 bg-obsidian border-platinum/15 text-platinum"
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <Label className="text-platinum/80 text-sm">Full Name (for audit log)</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Kevin · Tryant.ai (Partner Integration Engineer)"
                  className="mt-1.5 bg-obsidian border-platinum/15 text-platinum"
                  data-testid="invite-name-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-platinum/80 text-sm">Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger
                  className="mt-1.5 bg-obsidian border-platinum/15 text-platinum"
                  data-testid="invite-role-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-obsidian border-platinum/20 text-platinum">
                  {roleOptions.map((r) => (
                    <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isSuper && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Only a superuser can invite another admin / superuser.
                </p>
              )}
            </div>
            <div>
              <Label className="text-platinum/80 text-sm">Internal notes (optional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="What this person is being onboarded for"
                className="mt-1.5 bg-obsidian border-platinum/15 text-platinum"
                data-testid="invite-notes-input"
              />
            </div>
            <Button
              onClick={sendInvite}
              disabled={submitting}
              className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold disabled:opacity-40"
              data-testid="invite-send-btn"
            >
              {submitting ? (
                <><Loader2 size={14} className="mr-2 animate-spin" /> Sending…</>
              ) : (
                <><Mail size={14} className="mr-2" /> Send Invite Email</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* List */}
        <Card className="border-platinum/10 bg-navy-light/30">
          <CardHeader>
            <CardTitle className="text-platinum text-base font-serif">All Invites</CardTitle>
            <CardDescription className="text-platinum/60">
              Active, accepted, and revoked invites. Expired pending invites are pruned automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-slate-500 flex items-center"><Loader2 className="animate-spin mr-2" size={14} /> Loading…</div>
            ) : invites.length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center" data-testid="invite-empty">
                No invites yet. Send your first one above.
              </div>
            ) : (
              <div className="space-y-3" data-testid="invite-list">
                {invites.map((iv) => {
                  const sb = STATUS_BADGE[iv.status] || STATUS_BADGE.pending;
                  const expired = iv.status === "pending" && new Date(iv.expires_at) < new Date();
                  return (
                    <div
                      key={iv.id}
                      className="rounded-lg border border-platinum/10 bg-obsidian/40 p-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between"
                      data-testid={`invite-row-${iv.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-platinum font-medium truncate">{iv.email}</span>
                          <Badge className={`${sb.cls} border text-[10px] uppercase tracking-wider`}>
                            {expired ? "Expired" : sb.label}
                          </Badge>
                          <Badge className="bg-platinum/10 text-platinum/70 border border-platinum/20 capitalize text-[10px]">
                            {iv.role}
                          </Badge>
                        </div>
                        <p className="text-xs text-platinum/50 truncate mt-0.5">{iv.full_name}</p>
                        <p className="text-[11px] text-slate-500 mt-1">
                          Invited by {iv.inviter_email} · Created {new Date(iv.created_at).toLocaleString()}
                          {iv.accepted_at && <> · Accepted {new Date(iv.accepted_at).toLocaleString()}</>}
                          {iv.revoked_at && <> · Revoked {new Date(iv.revoked_at).toLocaleString()}</>}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-platinum/20 text-platinum hover:bg-platinum/10 bg-transparent"
                          onClick={() => copyInvitePreviewLink(iv)}
                          data-testid={`invite-copy-${iv.id}`}
                        >
                          <Copy size={12} className="mr-1" /> Copy Email
                        </Button>
                        {iv.status === "pending" && !expired && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-400/30 text-amber-200 hover:bg-amber-400/10 bg-transparent"
                              onClick={() => resend(iv.id)}
                              data-testid={`invite-resend-${iv.id}`}
                            >
                              <RotateCw size={12} className="mr-1" /> Resend
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-400/30 text-red-300 hover:bg-red-500/10 bg-transparent"
                              onClick={() => revoke(iv.id)}
                              data-testid={`invite-revoke-${iv.id}`}
                            >
                              <Ban size={12} className="mr-1" /> Revoke
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminUserInvitePage;
